import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Coins, CreditCard, Apple, Smartphone } from 'lucide-react';
import { useCredits } from '@/hooks/useCredits';
import { useTranslation } from 'react-i18next';
import { usePaymentPlatform } from '@/contexts/PaymentPlatformContext';

interface PurchaseCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PurchaseCreditsModal = ({ isOpen, onClose }: PurchaseCreditsModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { balance } = useCredits();
  const { isNative, paymentMethod } = usePaymentPlatform();

  // Determine platform-specific translation keys
  const getTitleKey = () => {
    if (!isNative) return 'credits.purchase.buyWithPayPal';
    return paymentMethod === 'apple' ? 'credits.purchase.buyWithApple' : 'credits.purchase.buyWithGoogle';
  };

  const getDescriptionKey = () => {
    return isNative ? 'credits.purchase.nativeDescription' : 'credits.purchase.paypalDescription';
  };

  const getButtonKey = () => {
    return isNative ? 'credits.purchase.viewStoreOptions' : 'credits.purchase.viewPayPalOptions';
  };

  const getNoticeKey = () => {
    return isNative ? 'credits.purchase.nativeDemoNotice' : 'credits.purchase.demoNotice';
  };

  const PaymentIcon = isNative 
    ? (paymentMethod === 'apple' ? Apple : Smartphone) 
    : CreditCard;

  const handleNavigateToPurchase = () => {
    onClose();
    navigate('/purchase-credits');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Coins className="h-6 w-6 text-yellow-500" />
            {t('credits.packagesTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('credits.purchase.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Balance */}
          <Alert className="mb-6">
            <Coins className="h-5 w-5" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-lg font-medium">
                {t('credits.currentBalance')}
              </span>
              <span className="text-2xl font-bold text-primary">
                {balance} {t('credits.title')}
              </span>
            </AlertDescription>
          </Alert>

          {/* Payment Option - Platform Aware */}
          <Card className="mb-6 border-primary">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl flex items-center justify-center gap-2">
                <PaymentIcon className="h-6 w-6" />
                {t(getTitleKey())}
              </CardTitle>
              <CardDescription>
                {t(getDescriptionKey())}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={handleNavigateToPurchase}
                className="w-full" 
                size="lg"
              >
                {t(getButtonKey())}
                <PaymentIcon className="h-5 w-5 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Platform-Aware Notice */}
          <Alert variant="default" className="bg-blue-50 dark:bg-blue-950/20 border-blue-200">
            <AlertDescription className="text-sm text-center">
              {t(getNoticeKey())}
            </AlertDescription>
          </Alert>
        </div>
      </DialogContent>
    </Dialog>
  );
};